import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, X, Upload, GripVertical, ImagePlus, Loader2 } from "lucide-react";
import { compressImageToBase64, checkImageQuality } from "@/lib/api";
import { toast } from "sonner";

const MIN_PHOTOS = 2;
const MAX_PHOTOS = 3;
const MAX_FILE_MB = 10;

// Optional tag chips the user can attach per photo (never required).
const TAG_OPTIONS = ["Front", "Back", "Tag", "Detail"];

export default function Uploader({
  images,
  setImages,
  brandLock,
  setBrandLock,
  plan,
  onGenerate,
  onDemo,
  scanning,
  canGenerate,
}) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  // Live list of valid images (nulls filtered)
  const list = (images || []).filter(Boolean);
  const count = list.length;
  const canAddMore = count < MAX_PHOTOS;

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const room = MAX_PHOTOS - count;
    let accepted = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    const rejected = Array.from(fileList).length - accepted.length;
    if (rejected > 0) toast.error(`${rejected} file(s) skipped — images only`);

    if (accepted.length > room) {
      toast.message(`Using your first ${room} photo${room === 1 ? "" : "s"}`);
      accepted = accepted.slice(0, room);
    }
    // Enforce max file size at the file boundary
    accepted = accepted.filter((f) => {
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        toast.error(`${f.name} is over ${MAX_FILE_MB}MB — skipped`);
        return false;
      }
      return true;
    });

    if (!accepted.length) return;
    setBusy(true);
    try {
      const results = [];
      for (const f of accepted) {
        // §2.3 Pre-flight rejection — blur / dark / size — before any API call.
        const q = await checkImageQuality(f);
        if (!q.ok) {
          toast.error(`${f.name}: ${q.reason} — please retake`);
          continue;
        }
        const { base64, preview } = await compressImageToBase64(f, 1600, 0.8);
        results.push({ base64, preview, tag: null });
      }
      if (!results.length) { setBusy(false); return; }
      setImages((arr) => {
        // Merge — keep existing slots (padded to MAX_PHOTOS) and add new after them
        const existing = (arr || []).filter(Boolean);
        const merged = [...existing, ...results].slice(0, MAX_PHOTOS);
        while (merged.length < MAX_PHOTOS) merged.push(null);
        return merged;
      });
    } catch (e) {
      toast.error("Couldn't process one of the photos");
    } finally {
      setBusy(false);
    }
  };

  const removeAt = (idx) => {
    setImages((arr) => {
      const existing = (arr || []).filter(Boolean);
      existing.splice(idx, 1);
      while (existing.length < MAX_PHOTOS) existing.push(null);
      return existing;
    });
  };

  const setTagAt = (idx, tag) => {
    setImages((arr) => {
      const existing = (arr || []).filter(Boolean).map((x, i) => (i === idx ? { ...x, tag } : x));
      while (existing.length < MAX_PHOTOS) existing.push(null);
      return existing;
    });
  };

  const reorder = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= count || to >= count) return;
    setImages((arr) => {
      const existing = (arr || []).filter(Boolean);
      const [moved] = existing.splice(from, 1);
      existing.splice(to, 0, moved);
      while (existing.length < MAX_PHOTOS) existing.push(null);
      return existing;
    });
  };

  const enough = count >= MIN_PHOTOS;
  const readyToGenerate = enough && canGenerate && !scanning && !busy;

  return (
    <div className="space-y-5">
      {/* Single drop zone */}
      <div
        data-testid="uploader-dropzone"
        onDragOver={(e) => { e.preventDefault(); if (canAddMore) setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          if (canAddMore) handleFiles(e.dataTransfer.files);
        }}
        onClick={() => canAddMore && inputRef.current?.click()}
        className={`relative rounded-2xl border-2 border-dashed p-6 sm:p-8 text-center cursor-pointer transition-colors ${
          drag
            ? "border-[#10B981] bg-[#10B981]/[0.06]"
            : canAddMore
            ? "border-white/12 hover:border-white/30 bg-white/[0.02]"
            : "border-white/8 bg-white/[0.01] cursor-not-allowed"
        }`}
        style={{ minHeight: "168px" }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          data-testid="uploader-file-input"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
        />
        <div className="flex flex-col items-center gap-2.5">
          <div className="w-11 h-11 rounded-xl bg-white/[0.06] flex items-center justify-center">
            {busy ? (
              <Loader2 size={20} className="text-white/70 animate-spin" />
            ) : (
              <ImagePlus size={20} className="text-white/70" />
            )}
          </div>
          <div className="text-white font-medium text-sm sm:text-base">
            {count === 0
              ? "Tap or drop 2–3 photos"
              : canAddMore
              ? `${count} added — add ${MAX_PHOTOS - count} more (max)`
              : `${count} photos ready — max reached`}
          </div>
          <div className="text-white/50 text-xs sm:text-sm max-w-md">
            Best results: a clear front shot plus a close-up of the brand or size tag.
          </div>
        </div>
      </div>

      {/* Thumbnail strip */}
      {count > 0 && (
        <div className="flex flex-wrap gap-2 sm:gap-3" data-testid="uploader-thumbs">
          {list.map((img, idx) => (
            <motion.div
              key={img.preview + idx}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              draggable
              onDragStart={(e) => { setDragIdx(idx); e.dataTransfer.effectAllowed = "move"; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); if (dragIdx !== null) reorder(dragIdx, idx); setDragIdx(null); }}
              onDragEnd={() => setDragIdx(null)}
              className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden group border border-white/10 bg-black"
              data-testid={`uploader-thumb-${idx}`}
            >
              <img src={img.preview} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
              {/* Hero badge on first photo */}
              {idx === 0 && (
                <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded-md bg-[#10B981]/90 text-black text-[9px] font-bold uppercase tracking-widest">
                  Hero
                </div>
              )}
              {/* Drag handle — hidden on mobile (tap-and-hold sort isn't a mobile pattern anyway) */}
              <div
                className="hidden sm:flex absolute top-1 right-8 w-6 h-6 rounded-md items-center justify-center cursor-move"
                style={{ background: "rgba(0,0,0,0.55)" }}
                title="Drag to reorder"
              >
                <GripVertical size={12} style={{ color: "rgba(255,255,255,0.9)" }} />
              </div>
              {/* Remove */}
              <button
                type="button"
                data-testid={`uploader-remove-${idx}`}
                onClick={(e) => { e.stopPropagation(); removeAt(idx); }}
                className="absolute top-1 right-1 w-6 h-6 rounded-md flex items-center justify-center transition-colors hover:!bg-red-500"
                style={{ background: "rgba(0,0,0,0.7)" }}
                aria-label="Remove photo"
              >
                <X size={12} style={{ color: "#FFFFFF" }} />
              </button>
              {/* Tag chip picker */}
              <div className="absolute bottom-1 left-1 right-1 flex flex-wrap gap-0.5">
                {TAG_OPTIONS.map((t) => (
                  <button
                    type="button"
                    key={t}
                    data-testid={`uploader-tag-${idx}-${t.toLowerCase()}`}
                    onClick={(e) => { e.stopPropagation(); setTagAt(idx, img.tag === t ? null : t); }}
                    style={
                      img.tag === t
                        ? { background: "#FFFFFF", color: "#000000" }
                        : { background: "rgba(0,0,0,0.6)", color: "rgba(255,255,255,0.9)" }
                    }
                    className="px-1.5 py-0.5 rounded text-[9px] font-semibold transition-colors"
                  >
                    {t}
                  </button>
                ))}
              </div>
              {scanning && <div className="laser-line" />}
            </motion.div>
          ))}
          {canAddMore && !busy && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              data-testid="uploader-add-more"
              className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl border-2 border-dashed border-white/12 hover:border-white/30 flex flex-col items-center justify-center gap-1 text-white/50 hover:text-white/80 transition-colors"
              aria-label="Add more photos"
            >
              <Upload size={18} />
              <span className="text-[10px] uppercase tracking-wider">Add</span>
            </button>
          )}
        </div>
      )}

      {/* Brand lock */}
      <div className="rounded-ds-md bg-surface-card border border-border-default shadow-card p-3 sm:p-4 md:p-5 flex items-center gap-3">
        <Camera size={16} className="text-ink-500 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] sm:text-xs uppercase tracking-widest text-ink-500 font-mono-num mb-0.5 sm:mb-1">
            Brand + item type<span className="hidden sm:inline"> (Optional — hugely boosts accuracy)</span>
          </div>
          <input
            data-testid="brand-lock-input"
            value={brandLock}
            onChange={(e) => setBrandLock(e.target.value)}
            placeholder={"e.g., Ralph Lauren puffer, Levi\u2019s jeans, Stussy hoodie\u2026"}
            className="w-full bg-transparent border-none outline-none text-ink-900 placeholder:text-ink-500/50 text-base"
          />
        </div>
      </div>

      {/* Cost transparency — the ONLY thing here that costs a credit is the
          studio photo later; scanning uses plan credits as labeled. Amber is
          reserved for cost/consent notices per DS v1.2. */}
      {!["reseller", "pro"].includes(plan) && (
        <div className="flex w-fit items-center gap-2 px-3.5 py-2 rounded-full bg-warning-soft border border-warning-fill/60 text-[12px] text-warning-text font-medium">
          <span aria-hidden="true">⚡</span>
          Studio photos cost 1 credit per item on Free &amp; Thrifter — included free on Reseller &amp; Pro.
        </div>
      )}

      {/* Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <button
          data-testid="generate-report-btn"
          onClick={onGenerate}
          disabled={!readyToGenerate}
          title={!enough ? `Add at least ${MIN_PHOTOS} photos` : ""}
          className={`sm:col-span-3 overflow-hidden py-4 sm:py-5 font-display font-bold text-base sm:text-lg tracking-wide transition-all rounded-[28px] ${
            !readyToGenerate
              ? "bg-accent-soft text-accent-text cursor-not-allowed"
              : "bg-accent-fill text-ink-900 hover:bg-accent-fill-hover shadow-cta"
          }`}
        >
          <span className="relative flex items-center justify-center gap-3">
            {enough ? "Generate Resell Report" : `Add at least ${MIN_PHOTOS} photos`}
          </span>
        </button>
        <button
          data-testid="try-demo-btn"
          onClick={onDemo}
          disabled={scanning}
          className="py-3.5 sm:py-5 rounded-[28px] font-semibold text-sm text-ink-700 bg-surface-card border border-border-strong hover:border-ink-900 transition-colors disabled:opacity-40"
        >
          Try Demo Scan
        </button>
      </div>
    </div>
  );
}
